import { ReactNode } from "react";
import { Box, styled, FormControl, InputAdornment, InputLabel, MenuItem, Select, SelectChangeEvent, SelectProps, TextField, TextFieldProps, CircularProgress } from '@mui/material'
import { SxProps } from '@mui/system';
import { ReduxState, useListContext } from 'ra-core';
import { ChangeEvent, Children, createContext, FC, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import { useSelector } from 'react-redux';
import SearchIcon from '@/icons/Search';
import { hEncode } from '@/utils/hashid';


const AtopFuncAreaBox = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  margin: theme.spacing(-1),
  padding: theme.spacing(2),
}))

const DatagridFuncPiece = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1),
  maxWidth: '100%',
}))


interface AtopFuncAreaControllerProps {
  setFilterVal: (sourceKey: string, filterVal: any) => void;
  values: Record<string, string | null>;
  remoteDataSource: boolean;
}

const AtopFuncAreaContext = createContext<AtopFuncAreaControllerProps>({
  setFilterVal: () => {},
  values: {},
  remoteDataSource: false,
})


const AtopFuncAreaContextProvider: FC<{ value: AtopFuncAreaControllerProps }> = ({ children, value }) => {
  return (
    <AtopFuncAreaContext.Provider value={value}>
      {children}
    </AtopFuncAreaContext.Provider>
  )
}


type CVal = string | ((x:any) => boolean) | null
interface CriteriaValues {
  listCtxFilters: { [k: string]: CVal },
  values: { [k: string]: string | null },
}
interface SetValAction {
  type: 'set_val',
  key: string,
  value: string | null,
}
interface PersistFiltersUpdateAction {
  type: 'persist_filters_update',
  persistFilters: any,
}
type AtopFuncAction = SetValAction | PersistFiltersUpdateAction


const useAtopFuncAreaContext = () => useContext(AtopFuncAreaContext);


export interface AtopFuncAreaProps {
  persistFilters?: any,
  remoteDataSource?: boolean,
  boxSx?: SxProps,
}

export const AtopFuncArea: FC<AtopFuncAreaProps> = ({
  persistFilters,
  children,
  remoteDataSource,
  boxSx,
}) => {
  const { setFilters } = useListContext()
  
  const filterDefs = useMemo(() => Children.map(children, (x) => {
    const x1 = x as React.ReactElement<DatagridFuncPieceProps>
    return x1?.props
  })?.filter(x => !!x), []) // ??
  
  const reducer = useCallback((prevCriterias: CriteriaValues, action: AtopFuncAction): CriteriaValues => {
    switch (action.type) {
      case 'set_val': {
        const def = (filterDefs || []).find(x => x.sourceKey === action.key)
        if (!def) { return prevCriterias }
  
        const listCtxFVal = action.value === defaultEmptyCriteriaVal(def) ? null
          : remoteDataSource ? action.value
          : def.match ? ((y: any) => !!def.match && def.match(y, action.value))
          : action.value
        const newState = {
          listCtxFilters: { ...prevCriterias.listCtxFilters, [action.key]: listCtxFVal },
          values: { ...prevCriterias.values, [action.key]: action.value },
        }
        setTimeout(() => setFilters(newState.listCtxFilters, {}))
        return newState
      }
      case 'persist_filters_update': {
        const newState = {
          listCtxFilters: { ...prevCriterias.listCtxFilters, ...action.persistFilters },
          values: prevCriterias.values,
        }
        setTimeout(() => (setFilters as Function)(newState.listCtxFilters, {}, undefined, true))
        return newState
      }
    }
    return prevCriterias
  }, [setFilters, filterDefs])

  const [criteriaValues, dispatch] = useReducer(reducer, undefined, () => (filterDefs || []).reduce(
    (acc, def) => {
      acc.values[def.sourceKey] = defaultEmptyCriteriaVal(def)
      return acc
    }, { 
      listCtxFilters: {...persistFilters },
      values: {} as Record<string, string | null>
    }))

  useEffect(() => {
    dispatch({
      type: 'persist_filters_update',
      persistFilters,
    })
  }, [persistFilters])

  const ctrl = useMemo(() => ({
    setFilterVal: (sourceKey:string, x:any) => {
      dispatch({
        type: 'set_val',
        key: sourceKey,
        value: x,
      })
    },
    remoteDataSource: !!remoteDataSource,
    values: criteriaValues.values,
  }), [!!remoteDataSource, criteriaValues.values])

  return (
    <AtopFuncAreaContextProvider value={ctrl}>
      <AtopFuncAreaBox sx={boxSx}>
        {children}
      </AtopFuncAreaBox>
    </AtopFuncAreaContextProvider>
  )
}

export interface SearchFieldProps extends DatagridFuncPieceProps {
  TextFieldProps?: TextFieldProps,
  sx?: SxProps,
}

export const SearchField: FC<SearchFieldProps> = ({
  type = 'search',
  sourceKey,
  transform,
  TextFieldProps = {},
  sx,
}) => {
  const {setFilterVal, values, remoteDataSource} = useAtopFuncAreaContext()
  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    setFilterVal(sourceKey, transform ? transform(input) : input)
  }, [setFilterVal, sourceKey, transform])
  const uid = useMemo(() => 'table-search-' + hEncode(new Date().getMilliseconds()), [])

  const loading = useSelector<ReduxState, boolean>(store => remoteDataSource && !!store.admin.loading)

  return (
    <DatagridFuncPiece sx={sx}>
      <TextField
        id={uid}
        fullWidth
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {loading ? <CircularProgress size={20} /> : <SearchIcon fontSize="small" />}
            </InputAdornment>
          )
        }}
        onChange={onChange}
        value={values[sourceKey]}
        variant="outlined"
        {...TextFieldProps}
      />
    </DatagridFuncPiece>
  )
}
SearchField.defaultProps = { type: 'search' }

export interface FilterFieldProps<T> extends DatagridFuncPieceProps<any, T> {
  label: string,
  filterOptions: T[],
  SelectProps?: SelectProps<T>,
  sx?: SxProps,
}

export interface FilterOption<TValue extends any = any> { value: TValue, label: string }

export const FilterField: FC<FilterFieldProps<FilterOption>> = ({
  type = 'filter',
  sourceKey,
  transform,
  label,
  filterOptions,
  SelectProps = {},
  sx,
}) => {
  const {setFilterVal, values} = useAtopFuncAreaContext()
  const onChange = useCallback((e: SelectChangeEvent<FilterOption>) => {
    const input = e.target.value
    setFilterVal(sourceKey, transform ? transform(input) : input)
  }, [setFilterVal, sourceKey, transform])
  const id_part_label = label.replaceAll(/\W/g, '-')
  
  return (
    <DatagridFuncPiece sx={sx}>
      <FormControl fullWidth>
        <InputLabel id={`${id_part_label}-filter-label`}>{label}</InputLabel>
        <Select
          fullWidth
          size="small"
          label={label}
          onChange={onChange}
          value={values[sourceKey] as any}
          variant="outlined"
          id={`${id_part_label}-filter-select`}
          {...SelectProps}
          inputProps={{
            ...SelectProps?.inputProps,
            labelId: `${id_part_label}-filter-label`
          }}
        >
          <MenuItem key="_empty_" value="_empty_">All</MenuItem>
          {filterOptions.map((item) => (
            <MenuItem
              key={JSON.stringify(item.value)}
              value={item as any}
            >
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </DatagridFuncPiece>
  )
}
FilterField.defaultProps = { type: 'filter' }

export const CustomFunctionField : FC<DatagridFuncPieceProps & { render: (x: AtopFuncAreaControllerProps) => ReactNode }> = ({
  type = 'search',
  sourceKey,
  render,
}) => {
  const ctx = useAtopFuncAreaContext()
  return (
    <>{render(ctx)}</>
  )
}


interface DatagridFuncPieceProps<Item extends any = any, FilterVal extends any = any> {
  type?: 'search' | 'filter';
  sourceKey: string;
  transform?: (x:any) => FilterVal;
  match?: (x:Item, filterVal:FilterVal) => boolean;
}

const defaultEmptyCriteriaVal = (x: DatagridFuncPieceProps) => {
  return x.type === 'search' ? '' : '_empty_'
}

